<template>
  <div>
    <div class="text--bold">{{ $t('views.Dashboard.Documentation.apiKey.title') }}</div>
    <action-input
      v-if="profile && profile.merchantId"
      :value="profile.merchantId"
      :disabled="true"
      label="Merchant ID:"
      :action-text="$t('views.Dashboard.Documentation.copy')"
      @click="copy(profile.merchantId)"
    />
    <action-input
      :value="apiKey"
      :disabled="true"
      label="API KEY:"
      :action-text="$t('views.Dashboard.Documentation.copy')"
      @click="copy(apiKey)"
    />
    <div class="row">
      <div class="col-9 d-flex">
        <a :href="swagerUrl" target="_blank"><v-button color="primary"><span class="icon-link mr-1"></span>{{ $t('views.Dashboard.Documentation.documentation') }}</v-button></a>
        <a :href="informationUrl" target="_blank" class="ml-2"><v-button color="primary" outline><span class="icon-info mr-1"></span>{{ $t('views.Dashboard.Documentation.information') }}</v-button></a>
      </div>
      <div class="col-3">
        <v-button color="error" width="full" outline @click="onGenereateNewApi()">{{ $t('views.Dashboard.Documentation.apiKey.generation') }}</v-button>
      </div>
    </div>

    <div class="divider my-2"></div>

    <div class="text--bold">{{ $t('views.Dashboard.Documentation.contactExternalGuids.title') }}</div>
    <action-input
      v-for="(contractItem, index) in contracts"
      :key="index"
      :value="contractItem.externalGuid"
      :disabled="true"
      :label="contractItem.partnerEntity.inn + ' / ' + currencySymbol(contractItem.currency)"
      :action-text="$t('views.Dashboard.Documentation.copy')"
      @click="copy(contractItem.externalGuid)"
    />

    <div class="divider my-2"></div>

    <div class="text--bold">{{ $t('views.Dashboard.Documentation.integrationWithTilda.title') }}</div>

    <div class="row">
      <div class="col-8">
        <autocomplete
          class="mb-1"
          :placeholder="$t('views.Dashboard.Documentation.integrationWithTilda.selectContract')"
          :items="contracts"
          value-field="id"
          item-text="fullName"
          v-model="selectedContract"
          emit-object
          not-empty
        ></autocomplete>
        <label>{{ $t('views.Dashboard.Documentation.integrationWithTilda.copyEmbedCode') }}</label>
        <v-textarea
          class="mt-1"
          :disabled="true"
          :rows="13"
          placeholer="Embed Code"
          v-model="embedCode"
        >
        </v-textarea>
        <div class="d-flex mt-1">
          <a :href="instructionLink" target="_blank"><v-button color="primary"><span class="icon-invoice mr-1"></span>{{ $t('views.Dashboard.Documentation.instructions') }}</v-button></a>
          <v-button class="ml-2" @click="copy(embedCode)">{{ $t('views.Dashboard.Documentation.copy') }}</v-button>
        </div>
      </div>
      <div class="col-4">
        <div class="d-flex mb-1">
          <v-input
            v-model="whiteDomain"
            :label="$t('views.Dashboard.Documentation.integrationWithTilda.addDomain')"
            :placeholder="$t('views.Dashboard.Documentation.integrationWithTilda.domainType')"
          ></v-input>
          <v-button class="ml-2" outline @click="onAddWhiteDomain"><span class="icon-plus"></span></v-button>
        </div>
        <label>{{ $t('views.Dashboard.Documentation.integrationWithTilda.domain') }}</label>
        <list-item v-for="(domain, domainIndex) in domainList" :key="domainIndex">
          <template v-slot:firstColumn>
            <div class="text--left">{{domain.domain}}</div>
          </template>

          <template v-slot:controls>
            <action-link icon-left="icon-trash" size="small" color="error" @click="onDeleteWhiteDomain(domain.id)"></action-link>
          </template>
        </list-item>
      </div>
    </div>

    <div class="divider my-2"></div>

    <div class="text--bold">{{ $t('views.Dashboard.Documentation.integrationModules.title') }}</div>
    <div class="row">
      <div class="col-12">
        <list-item three-columns>
          <template v-slot:firstColumn>
            <div class="text--left">{{ $t('views.Dashboard.Documentation.integrationModules.cmsName') }}</div>
          </template>

          <template v-slot:secondColumn>
            <div class="text--center">{{ $t('views.Dashboard.Documentation.integrationModules.downloadInstall') }}</div>
          </template>

          <template v-slot:thirdColumn>
            <div class="text--center">{{ $t('views.Dashboard.Documentation.integrationModules.instructions') }}</div>
          </template>
        </list-item>
        <list-item three-columns v-for="(module_item, module_index) in modulesData" :key="module_index">
          <template v-slot:firstColumn>
            <div class="text--left">{{module_item.name}}</div>
          </template>

          <template v-slot:secondColumn>
            <div class="text--center">
              <a :href="module_item.download" target="_blank">{{ $t('views.Dashboard.Documentation.integrationModules.link') }}</a>
            </div>
          </template>

          <template v-slot:thirdColumn>
            <div class="text--center" v-if="module_item.instruction">
              <a :href="module_item.instruction" target="_blank">{{ $t('views.Dashboard.Documentation.integrationModules.open') }}</a>
            </div>
          </template>
        </list-item>
      </div>
    </div>

    <div class="divider my-2"></div>

    <div class="text--bold">{{ $t('views.Dashboard.Documentation.Description.title') }}</div>
    <div>
      <p>{{ $t('views.Dashboard.Documentation.Description.content') }}</p>
    </div>
  </div>
</template>
<script>
import vInput from '@/ui/controls/vInput'
import vTextarea from '@/ui/controls/vTextarea'
import vButton from '@/ui/controls/vButton'
import ActionInput from '@/ui/controls/ActionInput'
import ListItem from '@/ui/slots/List/ListItem'
import ActionLink from '@/ui/controls/ActionLink'
import Autocomplete from '@/ui/controls/Autocomplete'

import currencySymbol from '@/mixins/currencySymbol'
import profile from '@/mixins/profile'
import hasActiveContracts from '@/mixins/hasActiveContracts'
import staticBaseUrl from '@/mixins/staticBaseUrl'
import reqCountry from '@/mixins/reqCountry'
import copyClipboard from '@/mixins/copyClipboard'
import contracts from '@/mixins/contracts'
import currContractId from '@/mixins/currContractId'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'

import { WhiteListDomainsGQL, IntegrationGQL } from '@/graphql/queries'
import { DeleteWhiteListDomainGQL, AddWhiteListDomainGQL, GenerateNewAPIKeyGQL } from '@/graphql/mutations'
import staticBackendUrl from '@/mixins/staticBackendUrl'

export default {
  components: {
    vInput,
    vTextarea,
    vButton,
    ListItem,
    ActionLink,
    Autocomplete,
    ActionInput
  },

  mixins: [
    currencySymbol,
    profile,
    hasActiveContracts,
    staticBaseUrl,
    staticBackendUrl,
    reqCountry,
    copyClipboard,
    contracts,
    currContractId,
    notifyGraphqlError
  ],

  computed: {
    swagerUrl () {
      return this.staticBackendUrl + '/swagger-ui.html'
    },
    informationUrl () {
      return this.staticBaseUrl + '/files/merchant/integration-rules.pdf'
    },
    contractOptions () {
      if (!this.contracts) {
        return []
      }

      return this.contracts.map((contract) => ({
        value: contract,
        text: contract.partnerEntity.inn + ' / ' + this.currencySymbol(contract.currency)
      }))
    },
    domainList () {
      if (!this.whiteListDomains) {
        return []
      }

      return this.whiteListDomains
    },
    modulesData () {
      return [
        {
          name: 'Drupal + Commerce',
          download: 'https://www.drupal.org/project/switips_commerce/releases/1.0.x-dev',
          instruction: ''
        },
        {
          name: 'Drupal + Uber Cart',
          download: 'https://www.drupal.org/project/switips_ubercart/releases/1.0.x-dev',
          instruction: ''
        },
        {
          name: 'OpenCart',
          download: 'https://www.opencart.com/index.php?route=marketplace/extension/info&extension_id=41563',
          instruction: ''
        },
        {
          name: 'МОDX + Minishop',
          download: 'https://modstore.pro/packages/discounts/switips',
          instruction: ''
        },
        {
          name: 'Joomla + JBzoo',
          download: 'https://extensions.joomla.org/extension/switips-api-for-jbzoo/',
          instruction: ''
        },
        {
          name: 'Joomla + Virtuemart',
          download: 'https://extensions.joomla.org/extension/e-commerce/e-commerce-integrations/switips-api-for-virtuemart/',
          instruction: ''
        },
        {
          name: 'Joomla + JoomShopping',
          download: 'https://extensions.joomla.org/extension/ads-a-affiliates/affiliate-systems/switips-api-for-joomshopping/',
          instruction: ''
        },
        {
          name: 'Joomla + HikaShop',
          download: 'https://extensions.joomla.org/extension/access-a-security/switips-api-for-hikashop/',
          instruction: ''
        },
        {
          name: 'CS-Cart',
          download: 'https://marketplace.cs-cart.com/switips-api.html',
          instruction: ''
        },
        {
          name: 'Shop-Script',
          download: 'https://www.webasyst.ru/store/plugin/shop/switips/',
          instruction: ''
        },
        {
          name: '1c-bitrix',
          download: this.staticBaseUrl + '/files/merchant/bitrix-merchant-module.zip',
          instruction: this.staticBaseUrl + '/files/merchant/bitrix-merchant-module-instruction.pdf'
        }
      ]
    },
    embedCode () {
      if (!this.selectedContract) {
        return ''
      }

      const externalGuid = this.selectedContract ? this.selectedContract.externalGuid : null
      const currency = this.selectedContract ? this.selectedContract.currency : null
      const merchantId = this.profile.merchantId
      const staging = window.location.host.startsWith('staging-office.switips.com') || window.location.host.startsWith('staging-office.myswitplanet.com') || window.location.host.startsWith('staging-merchant.myswitplanet.com') || window.location.host.startsWith('swi.tips') || window.location.host.startsWith('localhost')

      /* eslint-disable */
      return `
<!-- MYSPLIFE Merchant code -->
<script type="text/javascript">
  ;(function (window, document, jQuery, tag, path, merchantID, partnerContractGuid, currency, el, fel) {
      el = document.createElement(tag);fel = document.getElementsByTagName(tag)[0];el.async = true;el.src = path;fel.parentNode.insertBefore(el, fel);
      window.onload = function () {window.$SPMerchant.init(window, document, jQuery, window.$smOpts(merchantID, partnerContractGuid, currency${staging ? `, 'staging'` : ''}))}
  })(window, document, $, 'script', 'https://files.switips.com/src/mspmtilda10.bundle.js', '${merchantId}', '${externalGuid}', '${currency}')
<\/script>
<!-- /MYSPLIFE Merchant end -->
      `
      /* eslint-enable */
    },
    instructionLink () {
      const locale = this.$i18n.locale
      let prefUSA = ''
      if (this.reqCountry.USA) prefUSA = '-usa'
      return `${this.staticBaseUrl}/files/merchant/tilda-instruction/${locale}${prefUSA}.pdf`
    }
  },

  data () {
    return {
      selectedContract: this.currContractId && this.contracts ? this.contracts.find((contract) => contract.id === this.currContractId) : null,
      whiteDomain: null,
      apiKey: null
    }
  },

  apollo: {
    whiteListDomains: {
      query: WhiteListDomainsGQL,
      update: data => data.merchant.integration.tilda.whiteListDomains,
      skip () { return !this.hasActiveContracts }
    },
    apiKey: {
      query: IntegrationGQL,
      update: data => data.merchant.integration.apiKey,
      skip () { return !this.hasActiveContracts }
    }
  },

  methods: {
    copy (content) {
      this.copyClipboard(content, this.$i18n.t('views.Dashboard.Documentation.notify.copied'))
    },
    onChagneSelect (option) {
      this.selectedContract = JSON.parse(JSON.stringify(option))
    },
    async onAddWhiteDomain () {
      const domain = this.whiteDomain

      if (!domain) {
        return
      }

      const index = this.domainList.findIndex(d => {
        return domain.match(d.domain)
      })

      if (index > -1) {
        this.$notify({
          type: 'negative',
          message: this.$i18n.t('views.Dashboard.Documentation.notify.domainExists'),
          delay: 150,
          timeout: 3000
        })
        return
      }

      try {
        await this.$apollo.mutate({
          mutation: AddWhiteListDomainGQL,
          variables: {
            params: {
              crm: 'TILDA',
              domain
            }
          },
          fetchPolicy: 'no-cache'
        })

        await this.$apollo.queries.whiteListDomains.refetch()
      } catch (e) {
        this.notifyGraphqlError(e)
      }
    },
    async onDeleteWhiteDomain (domainId) {
      try {
        await this.$apollo.mutate({
          mutation: DeleteWhiteListDomainGQL,
          variables: {
            domainId
          },
          fetchPolicy: 'no-cache'
        })

        await this.$apollo.queries.whiteListDomains.refetch()
      } catch (e) {
        this.notifyGraphqlError(e)
      }
    },
    async onGenereateNewApi () {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: GenerateNewAPIKeyGQL,
          fetchPolicy: 'no-cache'
        })

        this.apiKey = data.generateNewApiKey.secretKey
      } catch (e) {
        this.notifyGraphqlError(e)
      }
    }
  },

  watch: {
    currContractId (val) {
      if (val && this.contracts) {
        this.selectedContract = this.contracts.find((contract) => contract.id === val)
      }
    },
    contracts (val) {
      if (this.currContractId && val) {
        this.selectedContract = val.find((contract) => contract.id === this.currContractId)
      }
    }
  }
}

</script>
<style lang="scss">
.row {
  display: flex;
  flex-wrap: wrap;
  margin-left: -1rem;
  margin-right: -1rem;
  .col-12 {
    width: 100%;
    padding: 1rem;
  }

  .col-9 {
    @extend .col-12;
    @include respond(tab-port) {
      width: 75%;
    }
  }
  .col-3 {
    @extend .col-12;
    @include respond(tab-port) {
      width: 25%;
    }
  }
  .col-8 {
    @extend .col-12;
    @include respond(tab-land) {
      width: 66.666667%;
    }
  }
  .col-4 {
    @extend .col-12;
    @include respond(tab-land) {
      width: 33.333333%;
    }
  }
}
</style>
